import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Camera, FileCheck, Megaphone, ArrowRight, Zap, Clock, TrendingUp, ChevronRight } from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Process Step Component
const ProcessStep = ({ icon: Icon, title, description, stepNumber, isLast }) => (
  <div className="relative flex items-start gap-6">
    {/* Connector Line */}
    {!isLast && (
      <div 
        className="absolute left-6 top-16 w-0.5 h-24 hidden md:block"
        style={{ background: "linear-gradient(to bottom, var(--primary-300), rgba(15, 95, 255, 0.2))" }}
      />
    )}
    
    {/* Icon Circle */}
    <div 
      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 z-10"
      style={{ 
        background: "var(--primary-100)",
        border: "2px solid var(--primary-300)"
      }}
    >
      <Icon size={24} style={{ color: "white" }} />
    </div>
    
    {/* Content */}
    <div className="pb-12 flex-grow">
      <div className="flex items-center gap-3 mb-2">
        <span 
          className="text-sm font-semibold px-2 py-1 rounded"
          style={{ background: "var(--primary-100)", color: "white" }}
        >
          Step {stepNumber}
        </span>
      </div>
      <h3 
        className="text-2xl font-bold mb-3"
        style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
      >
        {title}
      </h3>
      <p style={{ color: "var(--text-200)", lineHeight: "1.7" }}>{description}</p>
    </div>
  </div>
);

// Benefit Card Component
const BenefitCard = ({ icon: Icon, title, description }) => (
  <div 
    className="card p-6 text-center"
    style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}
  >
    <div 
      className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
      style={{ background: "var(--primary-100)" }}
    >
      <Icon size={28} style={{ color: "white" }} />
    </div>
    <h4 
      className="text-lg font-bold mb-2"
      style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
    >
      {title}
    </h4>
    <p style={{ color: "var(--text-200)", fontSize: "0.95rem" }}>{description}</p>
  </div>
);

// Main Appraisal to Market Page Component
const AppraisalToMarketPage = () => {
  const steps = [
    {
      icon: Camera,
      stepNumber: "1",
      title: "Capture Everything",
      description: "During the appraisal, all required information and photos are captured — machine details, condition, hours, customer expectations, and more. Everything is gathered once, properly, at the point of inspection."
    },
    {
      icon: FileCheck,
      stepNumber: "2",
      title: "Value & Approve",
      description: "The appraisal data flows through to valuation. Managers review complete information with photos and notes, set values, and approve the trade. All the data stays connected and accessible."
    },
    {
      icon: Megaphone,
      stepNumber: "3",
      title: "Go Live",
      description: "Once the deal is done, the machine is already prepared for market. Images are selected, descriptions refined, and with a click, the listing is pushed live across your chosen channels."
    }
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-20 relative overflow-hidden">
          <div 
            className="absolute inset-0 z-0"
            style={{
              background: "radial-gradient(ellipse at center, rgba(15, 95, 255, 0.1) 0%, transparent 70%)"
            }}
          />
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "var(--primary-100)", color: "white" }}
              >
                <ArrowRight size={18} />
                The Complete Journey
              </span>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8 px-2"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
              >
                From Appraisal <span className="text-gradient">to Market</span>
              </h1>
              <p 
                className="text-xl max-w-3xl mx-auto"
                style={{ color: "var(--text-200)" }}
              >
                MachineryMarketed connects the entire journey from trade appraisal through to live 
                advertising — <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>without duplication or delays</span>.
              </p>
            </div>
          </div>
        </section>

        {/* The Flow Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <div 
                className="card p-10 mb-12"
                style={{ 
                  background: "var(--background-200)", 
                  borderColor: "rgba(15, 95, 255, 0.2)"
                }}
              >
                <p 
                  className="text-xl text-center"
                  style={{ color: "var(--text-100)", lineHeight: "1.8" }}
                >
                  The information and photos captured during the appraisal are carried forward 
                  through valuation and straight into marketing. There's{" "}
                  <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>no re-entering details</span>,{" "}
                  <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>no chasing images</span>, and{" "}
                  <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>no rebuilding listings</span> from scratch.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Process Steps */}
        <section className="py-16" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 
                className="text-3xl md:text-4xl font-bold text-center mb-12"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                The Journey
              </h2>
              
              {steps.map((step, index) => (
                <ProcessStep 
                  key={index} 
                  {...step} 
                  isLast={index === steps.length - 1}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                The Results
              </h2>
              <p style={{ color: "var(--text-200)" }}>
                Using the same data from start to finish creates real advantages
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <BenefitCard 
                icon={Zap}
                title="Removes Friction"
                description="No more switching between systems or hunting for information. Everything flows seamlessly."
              />
              <BenefitCard 
                icon={TrendingUp}
                title="Reduces Errors"
                description="Data entered once is data entered correctly. Consistent information across the entire process."
              />
              <BenefitCard 
                icon={Clock}
                title="Shortens Time-to-Market"
                description="Dramatically reduces the time between taking a trade and getting it in front of buyers."
              />
            </div>
          </div>
        </section>

        {/* Summary Statement */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="mb-8"
                style={{ 
                  display: "flex", 
                  justifyContent: "center", 
                  gap: "2rem",
                  flexWrap: "wrap"
                }}
              >
                <div className="text-center">
                  <span 
                    className="block text-4xl font-bold"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--primary-300)" }}
                  >
                    Less Admin
                  </span>
                </div>
                <div className="text-center">
                  <span 
                    className="block text-4xl font-bold"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--primary-300)" }}
                  >
                    Faster Exposure
                  </span>
                </div>
                <div className="text-center">
                  <span 
                    className="block text-4xl font-bold"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--primary-300)" }}
                  >
                    Quicker Turnover
                  </span>
                </div>
              </div>
              
              <p 
                className="text-2xl mb-8"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                That's how machinery should move.
              </p>
              
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/how-it-works" className="btn-outline">
                  See Full Process
                </Link>
                <Link to="/sales-workflow" className="btn-outline">
                  Sales Workflow
                </Link>
                <Link to="/#contact" className="btn-primary inline-flex items-center gap-2">
                  Request a Demo <ChevronRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/terms" className="footer-link text-sm">Terms</Link>
              <Link to="/cookies" className="footer-link text-sm">Cookies</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AppraisalToMarketPage;
